import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  CelebrationOutlined,
  Diversity3,
  Groups,
  ArrowForward
} from '@mui/icons-material';

const PageContainer = styled.div`
  background-color: var(--color-bg);
  min-height: 100vh;
  padding: 120px 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 6vw, 5rem);
  margin-bottom: 24px;
  color: var(--color-text);
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--color-text-light);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 100px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  padding: 40px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 12px;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-primary-blue);
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  }
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(2, 74, 159, 0.05);
  color: var(--color-primary-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const CardTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: var(--color-text);
`;

const CardText = styled.p`
  color: var(--color-text-light);
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: var(--color-primary-blue);
  color: white;
  padding: 80px 40px;
  border-radius: 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const Button = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: white;
  color: var(--color-primary-blue);
  padding: 16px 32px;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  margin-top: 32px;
  font-family: var(--font-sans);
  
  &:hover {
    background: #f8fafc;
  }
`;

const BecomeAMember = () => {
  const benefits = [
    {
      icon: CelebrationOutlined,
      title: "Exclusive Events",
      description: "Priority access to cultural celebrations, dinners, and members-only networking opportunities."
    },
    {
      icon: Diversity3,
      title: "Cultural Connection",
      description: "Immerse yourself in authentic Israeli experiences, traditions, and language exchanges."
    },
    {
      icon: Groups,
      title: "Community",
      description: "Join a supportive network of students and alumni. Your home away from home."
    }
  ];

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <PageTitle>Become a Member</PageTitle>
          <PageSubtitle>
            Join SHALOM and be part of a vibrant community that celebrates Israeli culture,
            fosters innovation, and creates lasting connections at EPFL.
          </PageSubtitle>
        </HeaderSection>

        <BenefitsGrid>
          {benefits.map((b, i) => (
            <BenefitCard
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <IconBox><b.icon style={{ fontSize: 32 }} /></IconBox>
              <CardTitle>{b.title}</CardTitle>
              <CardText>{b.description}</CardText>
            </BenefitCard>
          ))}
        </BenefitsGrid>

        <CTASection>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '2.5rem', marginBottom: '16px' }}>
            Ready to Join?
          </h2>
          <p style={{ maxWidth: '600px', margin: '0 auto', fontSize: '1.1rem', opacity: 0.9 }}>
            Fill out our membership form to get started. We can't wait to meet you.
          </p>
          <Button
            href="https://docs.google.com/forms/d/e/1FAIpQLSeHy03razs9hsFOcGt5DtFMvVCOlC9Y_OgSPIv-ycLOTHRwng/viewform?usp=sharing&ouid=115296206304748791924"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Join SHALOM Today <ArrowForward fontSize="small" />
          </Button>

          {/* Decorative Circle */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-10%',
            width: '400px',
            height: '400px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%'
          }} />
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default BecomeAMember;